// src/pages/fleet/admin/GpsProvidersPage.tsx — Administration → GPS Providers.
//
// RFCL's trucks run on three telematics systems, and a trip can only be reported
// to IFMS for a truck we can actually see. This page says, per provider, whether
// we can see it and how many trucks that covers.
//
// BlackBuck's base URL is editable here on purpose. It is an address, not a
// secret, so it can be corrected from a browser instead of waiting for someone
// with SSH access — their v3 host is filled in already, and the field is there
// for the day they move it. Credentials are not editable here and never will be;
// the username and password stay in the server's .env.
import React, { useCallback, useEffect, useState } from 'react';
import { getProviders, setProviderUrl, testProvider } from '../../../services/fleetApi';
import type { GpsProvider, ProviderTestResult } from '../../../services/fleetApi';
import { P } from '../fleetStyles';
import Icon from '../Icon';
import { apiError } from '../../../utils/errors';
import { css } from '../../../utils/css';

const fmt = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const TONE: Record<string, { fg: string; bg: string; label: string }> = {
  HEALTHY:  { fg: 'var(--fx-green)',  bg: 'var(--fx-green-soft)',  label: 'Healthy' },
  DEGRADED: { fg: 'var(--fx-orange)', bg: 'var(--fx-orange-soft)', label: 'Degraded' },
  DOWN:     { fg: 'var(--fx-red)',    bg: 'var(--fx-red-soft)',    label: 'Down' },
  UNKNOWN:  { fg: 'var(--fx-text-3)', bg: 'var(--fx-gray-soft)',   label: 'Not connected' },
};

export default function GpsProvidersPage() {
  const [rows, setRows] = useState<GpsProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<Record<string, ProviderTestResult>>({});

  const load = useCallback(() => {
    setLoading(true);
    getProviders()
      .then((r) => {
        setRows(r.data.providers);
        setDraft(Object.fromEntries(r.data.providers.map((p) => [p.id, p.baseUrl || ''])));
        setErr('');
      })
      .catch((e) => setErr(apiError(e, 'Could not load the providers')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (p: GpsProvider) => {
    setBusy(p.id); setErr('');
    try {
      await setProviderUrl(p.id, draft[p.id] ?? '');
      setResult((r) => ({ ...r, [p.id]: { ok: true, message: 'Saved. Press Test connection to check it.' } }));
      load();
    } catch (e) {
      setErr(apiError(e, 'Could not save the address'));
    } finally { setBusy(null); }
  };

  const test = async (p: GpsProvider) => {
    setBusy(p.id); setResult((r) => ({ ...r, [p.id]: { ok: false, message: 'Testing…' } }));
    try {
      const r = await testProvider(p.id);
      setResult((x) => ({ ...x, [p.id]: r.data.result }));
    } catch (e) {
      setResult((x) => ({ ...x, [p.id]: { ok: false, message: apiError(e, 'The test could not be run') } }));
    } finally { setBusy(null); }
  };

  return (
    <div style={P.page}>
      <div style={P.head}>
        <div style={S.headLeft}>
          <div style={S.headIcon}><Icon name="antenna" size={19} stroke={2.1} /></div>
          <div>
            <h1 style={P.h1}>GPS Providers</h1>
            <p style={P.sub}>The three telematics feeds — what is connected, and what each one covers</p>
          </div>
        </div>
        <button type="button" style={P.btnGhost} onClick={load} disabled={loading}>
          <Icon name="refresh" size={14} stroke={2.2} /> Refresh
        </button>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {loading && !rows.length && <div style={S.muted}>Loading…</div>}

      {rows.map((p) => {
        const t = TONE[p.status] || TONE.UNKNOWN;
        const r = result[p.id];
        return (
          <section key={p.id} style={S.card}>
            <div style={S.cardHead}>
              <div>
                <div style={S.name}>{p.name}</div>
                <div style={S.auth}>{p.auth}</div>
              </div>
              <span style={{ ...S.pill, color: t.fg, background: t.bg }}>
                {p.configured ? t.label : 'Not configured'}
              </span>
            </div>

            <div style={S.stats}>
              <Stat label="Trucks in master" value={p.trucks} />
              <Stat label="IFMS whitelisted" value={p.whitelisted} tint={p.whitelisted ? 'var(--fx-green)' : undefined} />
              <Stat label="Last poll" value={fmt(p.lastPollAt)} small />
              <Stat label="Last success" value={fmt(p.lastSuccessAt)} small />
            </div>

            {p.lastError && <div style={S.lastErr}>Last error: {p.lastError}</div>}

            {p.editableBaseUrl && (
              <div style={S.editor}>
                <label style={S.label}>Base URL</label>
                <div style={S.row}>
                  <input
                    style={S.input}
                    value={draft[p.id] ?? ''}
                    placeholder="https://api-fms.blackbuck.com/fmsiot/api/v3/shipper"
                    onChange={(e) => setDraft((d) => ({ ...d, [p.id]: e.target.value }))}
                  />
                  <button type="button" style={P.btnPrimary} disabled={busy === p.id} onClick={() => save(p)}>Save</button>
                  <button type="button" style={P.btnGhost} disabled={busy === p.id || !p.baseUrl} onClick={() => test(p)}>
                    Test connection
                  </button>
                </div>
                <div style={S.hint}>
                  This is BlackBuck's v3 tracking host; the path{' '}
                  <code style={S.code}>/tracking/list</code> is added automatically. Change it only if they
                  give you a different address — the username and password live on the server, not here.
                </div>
                {p.note && <div style={S.note}>{p.note}</div>}
                {r && (
                  <div style={{ ...S.result,
                    background: r.ok ? 'var(--fx-green-soft)' : 'var(--fx-orange-soft)',
                    borderColor: r.ok ? 'var(--fx-green)' : 'var(--fx-orange)',
                    color: r.ok ? 'var(--fx-green)' : 'var(--fx-orange)' }}>
                    <b>{r.ok ? '✓ ' : '• '}{r.message}</b>
                    {r.ms != null && <span style={S.ms}> · {r.ms} ms</span>}
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function Stat({ label, value, small, tint }: { label: string; value: React.ReactNode; small?: boolean; tint?: string }) {
  return (
    <div>
      <div style={{ ...S.statVal, ...(small ? S.statValSmall : {}), ...(tint ? { color: tint } : {}) }}>{value}</div>
      <div style={S.statLbl}>{label}</div>
    </div>
  );
}

const S = css({
  headLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  headIcon: {
    width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--fx-accent-soft)', color: 'var(--fx-accent)', flexShrink: 0,
  },
  err: { color: 'var(--fx-red)', background: 'var(--fx-red-soft)', borderRadius: 10, fontSize: 13, padding: '10px 14px', marginBottom: 14 },
  muted: { color: 'var(--fx-text-2)', fontSize: 13, padding: '20px 2px' },

  card: { background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14, marginBottom: 14, overflow: 'hidden', boxShadow: 'var(--fx-shadow)' },
  cardHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, padding: '14px 18px', borderBottom: '1px solid var(--fx-border)' },
  name: { fontSize: 15, fontWeight: 700, color: 'var(--fx-text)' },
  auth: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 2 },
  pill: { fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 20, whiteSpace: 'nowrap' },

  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 14, padding: '14px 18px' },
  statVal: { fontSize: 19, fontWeight: 700, color: 'var(--fx-text)', letterSpacing: '-0.01em' },
  statValSmall: { fontSize: 13, fontWeight: 600 },
  statLbl: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 2 },
  lastErr: { fontSize: 12, color: 'var(--fx-red)', background: 'var(--fx-red-soft)', padding: '8px 18px', lineHeight: 1.5 },

  editor: { padding: '4px 18px 18px', borderTop: '1px solid var(--fx-border)' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fx-text-2)', margin: '14px 0 6px' },
  row: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  input: {
    flex: 1, minWidth: 240, padding: '9px 12px', borderRadius: 9, border: '1px solid var(--fx-border-strong)',
    background: 'var(--fx-input)', color: 'var(--fx-text)', fontSize: 13, outline: 'none', fontFamily: 'inherit',
  },
  hint: { fontSize: 11.5, color: 'var(--fx-text-2)', lineHeight: 1.6, marginTop: 9 },
  note: { fontSize: 11.5, color: 'var(--fx-orange)', background: 'var(--fx-orange-soft)', borderRadius: 9, padding: '8px 11px', marginTop: 9, lineHeight: 1.5 },
  code: { fontFamily: 'DM Mono, monospace', fontSize: 11, background: 'var(--fx-gray-soft)', padding: '1px 5px', borderRadius: 5 },
  result: { border: '1px solid', borderRadius: 9, padding: '9px 12px', marginTop: 10, fontSize: 12.5, lineHeight: 1.5 },
  ms: { opacity: 0.75, fontWeight: 400 },
});
